import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #042E4B 0%, #0A4A6F 100%);
  padding: 4rem;
`;

const Title = styled(motion.h1)`
  font-size: 4rem;
  font-weight: 700;
  color: white;
  margin-bottom: 1rem;
  text-align: center;
`;

const Subtitle = styled(motion.p)`
  font-size: 1.4rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 3rem;
  text-align: center;
`;

const CompetitorsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 3rem;
  max-width: 1400px;
  width: 100%;
  margin-bottom: 3rem;
`;

const RegionSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const RegionTitle = styled(motion.h3)`
  font-size: 2rem;
  color: #EF443C;
  font-weight: 700;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const CompetitorCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.2);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: #EF443C;
  }
`;

const CompetitorName = styled.div`
  font-size: 1.6rem;
  font-weight: 700;
  color: #042E4B;
  margin-bottom: 0.8rem;
`;

const CompetitorDetails = styled.div`
  font-size: 1.1rem;
  color: #607C90;
  line-height: 1.6;
`;

const DifferentialSection = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 2.5rem 3rem;
  max-width: 1400px;
  width: 100%;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
`;

const DifferentialTitle = styled.div`
  font-size: 1.6rem;
  color: #EF443C;
  font-weight: 700;
  margin-bottom: 1rem;
`;

const DifferentialText = styled.div`
  font-size: 1.2rem;
  color: #042E4B;
  line-height: 1.6;
  
  strong {
    color: #EF443C;
    font-weight: 700;
  }
`;

const Slide7Concorrentes: React.FC = () => {
  const brasilCompetitors = [
    {
      name: 'Planest',
      details: '6 anos no mercado • Foco em consultores e PMEs • Etapas divididas em menus',
    },
    {
      name: 'Mereo',
      details: 'Estrutura completa • Forte apelo visual • Foco em transformar visão em estratégia',
    },
  ];

  const exteriorCompetitors = [
    {
      name: 'Achieve It',
      details: 'Planejamento em formato de diagrama • Gestão de objetivos conectados',
    },
    {
      name: 'Cascade',
      details: 'Integra planos e subtarefas • Visualização de governança estratégica',
    },
    {
      name: 'Moon Camp',
      details: 'Centraliza estratégias em única tela • Mapa estratégico em diagrama',
    },
    {
      name: 'WorkBoard',
      details: 'Foco em execução de estratégia • Visualização em diagramas e tabelas',
    },
  ];

  return (
    <Container>
      <Title
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        Concorrentes
      </Title>
      <Subtitle
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        Benchmarking de Mercado
      </Subtitle>

      <CompetitorsGrid>
        <RegionSection>
          <RegionTitle
            initial={{ x: -30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.15 }}
          >
            🇧🇷 BRASIL
          </RegionTitle>
          {brasilCompetitors.map((comp, index) => (
            <CompetitorCard
              key={`brasil-${comp.name}`}
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.2 + index * 0.05 }}
            >
              <CompetitorName>{comp.name}</CompetitorName>
              <CompetitorDetails>{comp.details}</CompetitorDetails>
            </CompetitorCard>
          ))}
        </RegionSection>

        <RegionSection>
          <RegionTitle
            initial={{ x: -30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.25 }}
          >
            🌍 EXTERIOR
          </RegionTitle>
          {exteriorCompetitors.map((comp, index) => (
            <CompetitorCard
              key={`exterior-${comp.name}`}
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.3 + index * 0.05 }}
            >
              <CompetitorName>{comp.name}</CompetitorName>
              <CompetitorDetails>{comp.details}</CompetitorDetails>
            </CompetitorCard>
          ))}
        </RegionSection>
      </CompetitorsGrid>

      <DifferentialSection
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.6 }}
      >
        <DifferentialTitle>🎯 Nosso Diferencial</DifferentialTitle>
        <DifferentialText>
          <strong>ThinkWay</strong> é mais objetiva e descomplicada que os concorrentes. 
          Além de intuitiva, é <strong>consultiva</strong>: guia automaticamente o usuário 
          nos próximos passos, eliminando barreiras para empresários que não contam com consultores.
        </DifferentialText>
      </DifferentialSection>
    </Container>
  );
};

export default Slide7Concorrentes;
